import moment from 'moment'
import { isEmpty } from 'lodash'
import {
  synapse,
  ModalActions,
  ScenarioPermissions,
  ScenarioProgressColumn,
  ScenarioPlatformActions
} from '@ublique/platform'
import { saveAs } from 'file-saver'
import { BASE_URL } from 'config'

const { openModal } = ModalActions
const { READ, UPDATE } = ScenarioPermissions
const {
  DELETE_SCENARIO,
  RESET_SCENARIO,
  CANCEL_SCENARIO,
  WARNING_SCENARIO,
  PROGRESS_SCENARIO,
  KILL_SCENARIO
} = ScenarioPlatformActions
const { PROGRESS, PLAY, OUTPUT, ERROR, LOADING } = ScenarioProgressColumn

const showOptOutput = async ({ data, history }) => {
  const { id } = data
  history.push(`/output/${id}`)
}

const makeDownloadOutput =
  (i18n) =>
  async ({ data }) => {
    const { id } = data
    const lang = i18n.language

    const { body, fileName } = await synapse.fetch(
      `${BASE_URL}/reports/_/report/excel`,
      { method: 'POST' },
      {
        planId: null,
        scenarioCode: id,
        zip: true,
        lang
      }
    )
    saveAs(body, fileName)
  }

const consolidateScenario = async ({ data }) => {
  const { id } = data
  const scenarioStatus = 'CONSOLIDATING'

  const outputsBody = {
    scenarioStatus,
    scenarioCode: id
  }

  const body = {
    data: {},
    scenarioManager: { status: scenarioStatus }
  }

  await synapse.fetch(
    `${BASE_URL}/output/${id}`,
    { method: 'PUT' },
    outputsBody
  )

  await synapse.fetch(`${BASE_URL}/scenarios/${id}`, { method: 'PUT' }, body)
}

const cancelConsolidate = async ({ data }) => {
  const { id } = data
  const scenarioStatus = 'DONE'

  const outputsBody = {
    scenarioStatus: 'DRAFT',
    scenarioCode: id
  }

  const body = {
    data: {},
    scenarioManager: { status: scenarioStatus }
  }

  await synapse.fetch(
    `${BASE_URL}/output/${id}`,
    { method: 'PUT' },
    outputsBody
  )

  await synapse.fetch(`${BASE_URL}/scenarios/${id}`, { method: 'PUT' }, body)
}

const frozeScenario = async ({ data }) => {
  const { id } = data
  const scenarioStatus = 'FREEZED'

  const outputsBody = {
    scenarioStatus,
    scenarioCode: id
  }

  const body = {
    data: {},
    scenarioManager: { status: scenarioStatus }
  }

  await synapse.fetch(
    `${BASE_URL}/output/${id}`,
    { method: 'PUT' },
    outputsBody
  )

  await synapse.fetch(`${BASE_URL}/scenarios/${id}`, { method: 'PUT' }, body)
}

const sendMail = async ({ data }) => {
  const { id } = data
  await synapse.fetch(`${BASE_URL}/output/${id}/mail`, {
    method: 'POST'
  })
}

const sendSms = ({ data }) => {
  const { id } = data
  synapse.fetch(`${BASE_URL}/output/${id}/sms`, {
    method: 'POST'
  })
}

export const getTableActions = (t, i18n) => {
  const downloadOutput = makeDownloadOutput(i18n)
  return {
    WAIT: {
      column: LOADING,
      menuActions: [
        {
          title: t('Table.Menu.cancelScenario'),
          action: CANCEL_SCENARIO,
          permission: READ
        },
        {
          title: t('Table.Menu.killScenario'),
          action: KILL_SCENARIO,
          permission: READ
        }
      ]
    },
    IMPORTING: {
      column: LOADING,
      menuActions: []
    },
    BUILDING: {
      column: PROGRESS,
      menuActions: []
    },
    CLONING: {
      column: PROGRESS,
      menuActions: []
    },
    SAVING: {
      column: PROGRESS,
      menuActions: []
    },
    DELETING: {
      column: PROGRESS,
      menuActions: []
    },
    RUNNING: {
      column: PROGRESS,
      menuActions: [
        {
          title: t('Table.Menu.cancelScenario'),
          action: CANCEL_SCENARIO,
          permission: READ
        },
        {
          title: t('Table.Menu.killScenario'),
          action: KILL_SCENARIO,
          permission: READ
        },
        {
          title: t('Table.Menu.warningScenario'),
          action: WARNING_SCENARIO,
          permission: READ
        },
        {
          title: t('Table.Menu.progressScenario'),
          action: PROGRESS_SCENARIO,
          permission: READ
        }
      ]
    },
    CONSOLIDATING: {
      column: PROGRESS,
      menuActions: []
    },
    READY: {
      column: PLAY,
      menuActions: [
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        },
        {
          title: t('Table.Menu.importOrders'),
          action: ({ data }) => {
            openModal('uploadOrders', data)
          },
          permission: UPDATE
        }
      ]
    },
    CREATED: {
      column: PLAY,
      menuActions: [
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        },
        {
          title: t('Table.Menu.importOrders'),
          action: ({ data }) => {
            openModal('uploadOrders', data)
          },
          permission: UPDATE
        }
      ]
    },
    DONE: {
      column: OUTPUT,
      menuActions: [
        {
          title: t('Table.Menu.showOptOutput'),
          action: showOptOutput,
          permission: READ
        },
        {
          title: t('Table.Menu.downloadOutput'),
          action: downloadOutput,
          permission: READ
        },
        {
          title: t('Table.Menu.consolidateScenario'),
          action: consolidateScenario,
          permission: UPDATE
        },
        {
          title: t('Table.Menu.frozeScenario'),
          action: frozeScenario,
          permission: UPDATE
        },
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        },
        {
          title: t('Table.Menu.resetScenario'),
          action: RESET_SCENARIO
        },
        {
          title: t('Table.Menu.warningScenario'),
          action: WARNING_SCENARIO,
          permission: READ,
          forcePermission: true
        },
        {
          title: t('Table.Menu.progressScenario'),
          action: PROGRESS_SCENARIO,
          permission: READ,
          forcePermission: true
        }
      ]
    },
    CONSOLIDATED: {
      column: OUTPUT,
      menuActions: [
        {
          title: t('Table.Menu.cancelConsolidate'),
          action: cancelConsolidate,
          permission: UPDATE
        },
        {
          title: t('Table.Menu.frozeScenario'),
          action: frozeScenario,
          permission: UPDATE
        },
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        },
        {
          title: t('Table.Menu.showOptOutput'),
          action: showOptOutput,
          permission: READ
        },
        {
          title: t('Table.Menu.downloadOutput'),
          action: downloadOutput,
          permission: READ
        },
        {
          title: t('Table.Menu.sendMail'),
          action: sendMail,
          permission: READ
        },
        {
          title: t('Table.Menu.sendSms'),
          action: sendSms,
          permission: READ
        },
        {
          title: t('Table.Menu.warningScenario'),
          action: WARNING_SCENARIO,
          permission: READ,
          forcePermission: true
        },
        {
          title: t('Table.Menu.progressScenario'),
          action: PROGRESS_SCENARIO,
          permission: READ,
          forcePermission: true
        }
      ]
    },
    FREEZED: {
      column: OUTPUT,
      menuActions: [
        {
          title: t('Table.Menu.downloadOutput'),
          action: downloadOutput,
          permission: READ
        },
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        },
        {
          title: t('Table.Menu.sendMail'),
          action: sendMail,
          permission: READ
        },
        {
          title: t('Table.Menu.sendSms'),
          action: sendSms,
          permission: READ
        }
      ]
    },
    ERROR: {
      column: ERROR,
      menuActions: [
        {
          title: t('Table.Menu.resetScenario'),
          action: RESET_SCENARIO
        },
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        },
        {
          title: t('Table.Menu.warningScenario'),
          action: WARNING_SCENARIO,
          permission: READ
        },
        {
          title: t('Table.Menu.progressScenario'),
          action: PROGRESS_SCENARIO,
          permission: READ
        }
      ]
    },
    CREATE_ERROR: {
      column: ERROR,
      menuActions: [
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        }
      ]
    },
    CONSOLIDATE_ERROR: {
      column: ERROR,
      menuActions: [
        {
          title: t('Table.Menu.resetScenario'),
          action: cancelConsolidate,
          permission: UPDATE
        },
        {
          title: t('Table.Menu.deleteScenario'),
          action: DELETE_SCENARIO
        }
      ]
    }
  }
}
